import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/api';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import { useProducts } from '../product/ProductContext';
import { GENERAL_GUIDE, SOCIAL_GUIDES } from '../product/socialGuides';
import './social.css';

// The platforms light speed can post to, in the order they're shown. Glyphs are
// plain Material Symbols, not brand logos. Ids match the backend's platform ids.
const PLATFORMS = [
    { id: 'instagram', name: 'Instagram', icon: 'photo_camera', blurb: 'Reels, carousels and stories', hosts: ['instagram.com'], profileUrl: handle => `https://www.instagram.com/${handle}/` },
    { id: 'tiktok', name: 'TikTok', icon: 'music_note', blurb: 'Short vertical video', hosts: ['tiktok.com'], profileUrl: handle => `https://www.tiktok.com/@${handle}` },
    { id: 'youtube', name: 'YouTube', icon: 'smart_display', blurb: 'Shorts and long-form video', hosts: ['youtube.com', 'youtu.be'], profileUrl: handle => `https://www.youtube.com/@${handle}` },
    { id: 'linkedin', name: 'LinkedIn', icon: 'work', blurb: 'Posts, documents and articles', hosts: ['linkedin.com'] },
    { id: 'x', name: 'X', icon: 'tag', blurb: 'Posts and threads', hosts: ['x.com', 'twitter.com'], profileUrl: handle => `https://x.com/${handle}` },
    { id: 'threads', name: 'Threads', icon: 'alternate_email', blurb: 'Text posts and replies', hosts: ['threads.net', 'threads.com'], profileUrl: handle => `https://www.threads.net/@${handle}` },
    { id: 'facebook', name: 'Facebook', icon: 'groups', blurb: 'Page posts and reels', hosts: ['facebook.com', 'fb.com'], profileUrl: handle => `https://www.facebook.com/${handle}` },
    { id: 'pinterest', name: 'Pinterest', icon: 'push_pin', blurb: 'Pins and idea boards', hosts: ['pinterest.com'], profileUrl: handle => `https://www.pinterest.com/${handle}/` },
];

const platformById = (id) => PLATFORMS.find(platform => platform.id === id);
const platformName = (id) => platformById(id)?.name || id;

// One account per platform card: the one that works, then one that needs attention, then a manual one
const STATUS_RANK = { connected: 0, expired: 1, error: 2, manual: 3 };
const accountFor = (accounts, platformId) => accounts
    .filter(account => account.platform === platformId)
    .sort((a, b) => (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9))[0] || null;

const STATUS_CHIPS = {
    connected: { label: 'Connected', icon: 'check_circle', tone: 'is-success' },
    manual: { label: 'Linked manually', icon: 'edit_note', tone: 'is-neutral' },
    expired: { label: 'Expired — reconnect', icon: 'schedule', tone: 'is-warn' },
    error: { label: 'Needs attention', icon: 'error', tone: 'is-danger' },
    none: { label: 'Not connected', icon: 'radio_button_unchecked', tone: 'is-muted' },
};

const errorMessage = (error, fallback) => error?.response?.data?.error || fallback;

const formatDate = (value) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const hostMatches = (hostname, hosts) => {
    const host = hostname.toLowerCase().replace(/^www\./, '');
    return hosts.some(allowed => host === allowed || host.endsWith(`.${allowed}`));
};

const toUrl = (value) => {
    const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    try {
        const url = new URL(withScheme);
        return ['http:', 'https:'].includes(url.protocol) && url.hostname.includes('.') ? url : null;
    } catch (e) {
        return null;
    }
};

// Path segments that come before the name in profile links (linkedin.com/in/name, youtube.com/c/name)
const PATH_PREFIXES = ['in', 'company', 'school', 'showcase', 'c', 'channel', 'user', 'pg'];

// The handle in a pasted profile link: instagram.com/yourbrand → yourbrand
const handleFromUrl = (url) => {
    const segments = url.pathname.split('/').filter(Boolean).map(segment => decodeURIComponent(segment));
    const index = segments.length > 1 && PATH_PREFIXES.includes(segments[0].toLowerCase()) ? 1 : 0;
    return (segments[index] || '').replace(/^@+/, '');
};

const HANDLE_PATTERN = /^[\p{L}\p{N}._-]+$/u;

// Checks the manual form before it reaches the server. A profile link pasted
// into the handle field is accepted: its handle and link are both used.
const validateManual = (platform, handleInput, urlInput) => {
    let handle = handleInput.trim();
    let profileUrl = urlInput.trim();
    const looksLikeLink = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}\//i.test(handle);
    if (looksLikeLink) {
        const url = toUrl(handle);
        if (!url || !hostMatches(url.hostname, platform.hosts)) {
            return { error: `That isn't a ${platform.name} link. Enter just the handle, like @yourbrand.`, field: 'handle' };
        }
        if (!profileUrl) profileUrl = url.toString();
        handle = handleFromUrl(url);
    }
    handle = handle.replace(/^@+/, '');
    if (!handle) return { error: `Enter your ${platform.name} handle, like @yourbrand.`, field: 'handle' };
    if (/\s/.test(handle)) return { error: 'Handles can\'t contain spaces. Use the name after the @.', field: 'handle' };
    if (!HANDLE_PATTERN.test(handle)) return { error: 'Use only letters, numbers, dots, dashes and underscores.', field: 'handle' };
    if (handle.length > 60) return { error: 'That handle is too long. Check it and try again.', field: 'handle' };
    if (profileUrl) {
        const url = toUrl(profileUrl);
        if (!url) return { error: `Enter a full link, like ${platform.hosts[0]}/yourbrand, or leave it empty.`, field: 'url' };
        if (!hostMatches(url.hostname, platform.hosts)) {
            return { error: `That link isn't on ${platform.hosts[0]}. Paste your ${platform.name} profile link, or leave it empty.`, field: 'url' };
        }
        profileUrl = url.toString();
    }
    return { handle, profileUrl };
};

// The link to an account's profile: the one we were given, or one built from the handle
const profileLink = (account) => {
    if (!account) return '';
    if (account.profileUrl) return account.profileUrl;
    const build = platformById(account.platform)?.profileUrl;
    return build && account.handle ? build(account.handle) : '';
};

// ?connected=<platform> or ?error=<message>, set by the backend after OAuth
const readReturnNotice = (search) => {
    const params = new URLSearchParams(search);
    const connected = params.get('connected');
    const error = params.get('error');
    if (connected) return { severity: 'success', platform: connected };
    if (error) return { severity: 'error', message: error };
    return null;
};

const AccountAvatar = ({ account }) => {
    const [failed, setFailed] = useState('');
    const src = account.avatarUrl && account.avatarUrl !== failed ? account.avatarUrl : '';
    const initial = (account.displayName || account.handle || '?').replace(/^@/, '').charAt(0).toUpperCase();
    return (
        <span className="ls-social-avatar" aria-hidden="true">
            {src ? <img src={src} alt="" width={36} height={36} onError={() => setFailed(account.avatarUrl)} /> : initial}
        </span>
    );
};

const StatusChip = ({ status }) => {
    const chip = STATUS_CHIPS[status] || STATUS_CHIPS.none;
    return (
        <span className={`ls-social-status ${chip.tone}`}>
            <Icon name={chip.icon} size={14} filled={status === 'connected'} />
            {chip.label}
        </span>
    );
};

// What the card says about the account (or why there isn't one yet)
const AccountSummary = ({ platform, provider, account }) => {
    if (!account) {
        const note = provider?.configured
            ? provider.connectHint || `Sign in with ${platform.name} to connect. You can disconnect any time.`
            : 'Not available yet. Add it manually for now, so plans know you post here.';
        return (
            <>
                <div className="ls-social-blurb">{platform.blurb}</div>
                <div className="ls-social-note" title={provider?.configured ? undefined : provider?.reason}>
                    <Icon name={provider?.configured ? 'lock' : 'info'} size={15} />
                    <span>{note}</span>
                </div>
            </>
        );
    }
    const since = formatDate(account.connectedAt);
    const notes = {
        connected: [account.displayName, since && `Connected ${since}`].filter(Boolean).join(' · '),
        manual: provider?.configured
            ? 'Added by hand. Connect to let us read your results.'
            : 'Added by hand. Results can be read once direct connect is available.',
        expired: `Access expired${formatDate(account.expiresAt) ? ` ${formatDate(account.expiresAt)}` : ''}. Reconnect to keep reading results.`,
        error: 'We lost access to this account. Reconnect to fix it.',
    };
    return (
        <>
            <div className="ls-social-account">
                <AccountAvatar account={account} />
                <span className="ls-social-account-text">
                    <span className="ls-social-handle" title={`@${account.handle}`}>@{String(account.handle || '').replace(/^@/, '')}</span>
                    <span className="ls-social-account-sub">{notes[account.status] || since}</span>
                </span>
            </div>
            {account.status === 'manual' && !provider?.configured && (
                <div className="ls-social-note" title={provider?.reason}>
                    <Icon name="info" size={15} />
                    <span>Direct connect: not available yet.</span>
                </div>
            )}
        </>
    );
};

const ManualForm = ({ platform, busy, error, fieldError, values, onChange }) => (
    <div className="ls-social-manual">
        <input
            className={`ls-input is-compact${fieldError === 'handle' ? ' is-invalid' : ''}`}
            value={values.handle}
            onChange={event => onChange({ handle: event.target.value })}
            placeholder="@yourbrand"
            aria-label={`${platform.name} handle`}
            aria-invalid={fieldError === 'handle'}
            aria-describedby={`ls-social-manual-msg-${platform.id}`}
            autoComplete="off"
            spellCheck="false"
            disabled={busy}
            autoFocus
        />
        <input
            className={`ls-input is-compact${fieldError === 'url' ? ' is-invalid' : ''}`}
            value={values.profileUrl}
            onChange={event => onChange({ profileUrl: event.target.value })}
            placeholder={`Profile link (optional), ${platform.hosts[0]}/…`}
            aria-label={`${platform.name} profile link (optional)`}
            aria-invalid={fieldError === 'url'}
            aria-describedby={`ls-social-manual-msg-${platform.id}`}
            inputMode="url"
            autoComplete="off"
            spellCheck="false"
            disabled={busy}
        />
        <div
            id={`ls-social-manual-msg-${platform.id}`}
            className={`ls-social-manual-msg${error ? ' is-error' : ''}`}
            role={error ? 'alert' : undefined}
        >
            {error || 'The name after the @. Paste a profile link to fill both.'}
        </div>
    </div>
);

const PlatformCard = ({
    platform, provider, account, busy, error, manual,
    onConnect, onOpenManual, onManualChange, onManualSubmit, onManualCancel, onDisconnect, onGuide, onDismissError,
}) => {
    const status = account?.status || 'none';
    const configured = Boolean(provider?.configured);
    const isBusy = Boolean(busy);
    const link = profileLink(account);
    const unavailableTitle = configured ? undefined : provider?.reason || `Connecting ${platform.name} isn't available yet`;

    const connectButton = (label) => (
        <button
            type="button"
            className="ls-btn ls-btn-outline ls-social-connect"
            onClick={onConnect}
            disabled={!configured || isBusy}
            title={unavailableTitle}
        >
            {busy === 'connect' ? <span className="ls-spinner" /> : <Icon name={label === 'Connect' ? 'link' : 'sync'} size={18} />}
            {label}
        </button>
    );

    const viewProfile = link && (
        <a className="ls-btn ls-btn-ghost ls-social-ghost" href={link} target="_blank" rel="noopener noreferrer">
            View profile<Icon name="open_in_new" size={15} />
        </a>
    );

    const removeButton = (
        <button type="button" className="ls-btn ls-btn-ghost ls-social-ghost ls-social-end" onClick={onDisconnect} disabled={isBusy}>
            {status === 'manual' ? 'Remove' : 'Disconnect'}
        </button>
    );

    let actions;
    if (manual) {
        actions = (
            <>
                <button type="submit" className="ls-btn ls-btn-dark ls-social-save" disabled={isBusy}>
                    {busy === 'manual' ? <span className="ls-spinner" /> : <Icon name="check" size={18} />}
                    Save
                </button>
                <button type="button" className="ls-btn ls-btn-ghost ls-social-ghost" onClick={onManualCancel} disabled={isBusy}>
                    Cancel
                </button>
            </>
        );
    } else if (status === 'connected') {
        actions = (
            <>
                {link ? (
                    <a className="ls-btn ls-btn-outline ls-social-connect" href={link} target="_blank" rel="noopener noreferrer">
                        <Icon name="open_in_new" size={18} />View profile
                    </a>
                ) : null}
                {removeButton}
            </>
        );
    } else if (status === 'expired' || status === 'error') {
        actions = (
            <>
                {connectButton('Reconnect')}
                {removeButton}
            </>
        );
    } else if (status === 'manual') {
        actions = (
            <>
                {connectButton('Connect')}
                {viewProfile}
                {removeButton}
            </>
        );
    } else {
        actions = (
            <>
                {connectButton('Connect')}
                <button type="button" className="ls-btn ls-btn-ghost ls-social-ghost" onClick={onOpenManual} disabled={isBusy}>
                    <Icon name="edit" size={16} />Add manually
                </button>
            </>
        );
    }

    const titleId = `ls-social-title-${platform.id}`;
    const Wrapper = manual ? 'form' : 'div';
    const wrapperProps = manual ? { onSubmit: onManualSubmit, noValidate: true } : {};

    return (
        <section className={`ls-card ls-social-card is-${status}`} aria-labelledby={titleId} aria-busy={isBusy || undefined}>
            <div className="ls-social-card-head">
                <span className={`ls-social-glyph${status === 'connected' ? ' is-on' : ''}`} aria-hidden="true">
                    <Icon name={platform.icon} size={22} />
                </span>
                <span className="ls-social-card-name">
                    <h3 id={titleId} className="ls-card-title ls-social-name">{platform.name}</h3>
                    <StatusChip status={status} />
                </span>
                <button
                    type="button"
                    className="ls-btn ls-btn-icon ls-social-guide-btn"
                    onClick={onGuide}
                    aria-label={`${platform.name} setup guide`}
                    title="Setup guide"
                >
                    <Icon name="menu_book" size={20} />
                </button>
            </div>
            <Wrapper className="ls-social-card-main" {...wrapperProps}>
                <div className="ls-social-card-body">
                    {manual ? (
                        <ManualForm
                            platform={platform}
                            busy={busy === 'manual'}
                            error={manual.error}
                            fieldError={manual.field}
                            values={manual}
                            onChange={onManualChange}
                        />
                    ) : (
                        <AccountSummary platform={platform} provider={provider} account={account} />
                    )}
                </div>
                <div className="ls-social-card-actions">{actions}</div>
            </Wrapper>
            {error && <Alert severity="error" onClose={onDismissError}>{error}</Alert>}
        </section>
    );
};

const CardSkeleton = () => (
    <div className="ls-card ls-social-card" aria-hidden="true">
        <div className="ls-social-card-head">
            <span className="ls-skeleton ls-social-glyph" />
            <span className="ls-social-card-name">
                <span className="ls-social-name"><span className="ls-skeleton-text" style={{ width: 90 }} /></span>
                <span className="ls-skeleton" style={{ width: 104, height: 22, borderRadius: 999 }} />
            </span>
        </div>
        <div className="ls-social-card-main">
            <div className="ls-social-card-body">
                <span className="ls-social-blurb"><span className="ls-skeleton-text" style={{ width: '70%' }} /></span>
                <span className="ls-social-note"><span className="ls-skeleton-text" style={{ width: '90%' }} /></span>
            </div>
            <div className="ls-social-card-actions">
                <span className="ls-skeleton" style={{ width: 110, height: 38, borderRadius: 20 }} />
                <span className="ls-skeleton" style={{ width: 120, height: 20, borderRadius: 6 }} />
            </div>
        </div>
    </div>
);

const ConfirmDialog = ({ platform, account, isBusy, error, onConfirm, onCancel }) => {
    useEffect(() => {
        const handleKey = (event) => event.key === 'Escape' && !isBusy && onCancel();
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isBusy, onCancel]);

    const isManual = account.status === 'manual';
    const handle = `@${String(account.handle || '').replace(/^@/, '')}`;
    return (
        <div className="ls-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !isBusy && onCancel()}>
            <div
                className="ls-modal ls-social-dialog"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="ls-social-dialog-title"
                aria-describedby="ls-social-dialog-body"
            >
                <h2 id="ls-social-dialog-title" className="ls-social-dialog-title">
                    {isManual ? `Remove ${platform.name}?` : `Disconnect ${platform.name}?`}
                </h2>
                <div id="ls-social-dialog-body" className="ls-social-dialog-body">
                    {isManual ? (
                        <>We'll forget <strong>{handle}</strong> for this product. Nothing changes on {platform.name} itself.</>
                    ) : (
                        <>
                            light speed stops reading results from <strong>{handle}</strong> and won't be able to post there.
                            Your posts on {platform.name} stay as they are, and you can connect again any time.
                        </>
                    )}
                </div>
                {error && <Alert severity="error">{error}</Alert>}
                <div className="ls-social-dialog-actions">
                    <button type="button" className="ls-btn ls-btn-secondary" onClick={onCancel} disabled={isBusy} autoFocus>
                        Cancel
                    </button>
                    <button type="button" className="ls-btn ls-social-danger" onClick={onConfirm} disabled={isBusy}>
                        {isBusy ? <span className="ls-spinner" /> : <Icon name="link_off" size={18} />}
                        {isManual ? 'Remove' : 'Disconnect'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ---------- Setup guides ----------

const GeneralGuide = () => (
    <div className="ls-card is-flat ls-social-general">
        <div>
            <h3 className="ls-card-title ls-social-general-title">{GENERAL_GUIDE?.title || 'Before you start'}</h3>
            {GENERAL_GUIDE?.intro && <p className="ls-social-general-intro">{GENERAL_GUIDE.intro}</p>}
        </div>
        {GENERAL_GUIDE?.principles?.length > 0 && (
            <ol className="ls-social-principles">
                {GENERAL_GUIDE.principles.map((principle, index) => (
                    <li key={principle} className="ls-social-principle">
                        <span className="ls-social-principle-num" aria-hidden="true">{index + 1}</span>
                        <span>{principle}</span>
                    </li>
                ))}
            </ol>
        )}
    </div>
);

const PlatformGuide = ({ platform }) => {
    const guide = SOCIAL_GUIDES?.[platform.id];
    if (!guide) {
        return (
            <div className="ls-placeholder ls-social-guide-empty">
                <Icon name="menu_book" size={28} />
                <span>The {platform.name} guide is on its way.</span>
            </div>
        );
    }
    const create = guide.create || [];
    const warmup = guide.warmup || [];
    const avoid = guide.avoid || [];
    const links = guide.links || [];
    return (
        <div className="ls-social-guide-body ls-fade-in" key={platform.id}>
            <div className="ls-social-guide-col">
                {guide.accountType && (
                    <div className="ls-social-fact">
                        <Icon name="badge" size={20} />
                        <div>
                            <div className="ls-social-fact-label">Account type</div>
                            <div className="ls-social-fact-value">{guide.accountType}</div>
                        </div>
                    </div>
                )}
                {create.length > 0 && (
                    <div className="ls-social-block">
                        <h4 className="ls-social-block-title">Create the account</h4>
                        <ol className="ls-social-steps">
                            {create.map((step, index) => (
                                <li key={step} className="ls-social-step">
                                    <span className="ls-social-step-num" aria-hidden="true">{index + 1}</span>
                                    <span>{step}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                )}
                {guide.cadence && (
                    <div className="ls-social-fact">
                        <Icon name="event_repeat" size={20} />
                        <div>
                            <div className="ls-social-fact-label">Posting cadence</div>
                            <div className="ls-social-fact-value">{guide.cadence}</div>
                        </div>
                    </div>
                )}
            </div>
            <div className="ls-social-guide-col">
                {warmup.length > 0 && (
                    <div className="ls-social-block">
                        <h4 className="ls-social-block-title">Warm it up</h4>
                        <ol className="ls-social-timeline">
                            {warmup.map((phase, index) => (
                                <li key={phase.phase || index} className="ls-social-phase">
                                    <span className="ls-social-phase-dot" aria-hidden="true" />
                                    <div className="ls-social-phase-title">{phase.phase}</div>
                                    <ul className="ls-social-phase-steps">
                                        {(phase.steps || []).map(step => <li key={step}>{step}</li>)}
                                    </ul>
                                </li>
                            ))}
                        </ol>
                    </div>
                )}
            </div>
            {(avoid.length > 0 || links.length > 0) && (
                <div className="ls-social-guide-foot">
                    {avoid.length > 0 && (
                        <div className="ls-social-block ls-social-avoid">
                            <h4 className="ls-social-block-title">Avoid</h4>
                            <ul className="ls-social-avoid-list">
                                {avoid.map(item => (
                                    <li key={item}><Icon name="block" size={16} /><span>{item}</span></li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {links.length > 0 && (
                        <div className="ls-social-block">
                            <h4 className="ls-social-block-title">Official help</h4>
                            <ul className="ls-social-links">
                                {links.map(item => (
                                    <li key={item.url}>
                                        <a className="ls-link" href={item.url} target="_blank" rel="noopener noreferrer">
                                            {item.label}<Icon name="open_in_new" size={14} />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const GuideSection = ({ selected, onSelect, headingRef }) => {
    const platform = platformById(selected) || PLATFORMS[0];
    return (
        <section className="ls-social-guides" aria-labelledby="ls-social-guides-title">
            <div className="ls-social-guides-head">
                <h2 id="ls-social-guides-title" className="ls-social-guides-title" ref={headingRef} tabIndex={-1}>
                    How to create and warm up your accounts
                </h2>
                <p className="ls-social-guides-sub">New accounts that post right away get treated like spam. A few weeks of warm-up pays for itself.</p>
            </div>
            <GeneralGuide />
            <div className="ls-card ls-social-guide-card">
                <div className="ls-tabs ls-social-guide-tabs" role="tablist" aria-label="Platform guide">
                    {PLATFORMS.map(item => (
                        <button
                            key={item.id}
                            type="button"
                            role="tab"
                            id={`ls-social-tab-${item.id}`}
                            aria-selected={item.id === platform.id}
                            aria-controls="ls-social-guide-panel"
                            className={`ls-tab${item.id === platform.id ? ' is-active' : ''}`}
                            onClick={() => onSelect(item.id)}
                        >
                            <Icon name={item.icon} size={17} />{item.name}
                        </button>
                    ))}
                </div>
                <div className="ls-social-guide-select">
                    <label className="ls-label" htmlFor="ls-social-guide-platform">Platform</label>
                    <select
                        id="ls-social-guide-platform"
                        className="ls-select"
                        value={platform.id}
                        onChange={event => onSelect(event.target.value)}
                    >
                        {PLATFORMS.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </select>
                </div>
                <div id="ls-social-guide-panel" role="tabpanel" aria-labelledby={`ls-social-tab-${platform.id}`}>
                    <PlatformGuide platform={platform} />
                </div>
            </div>
        </section>
    );
};

// ---------- Screen ----------

const SocialAccountsScreen = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { currentProduct, replaceProduct } = useProducts();
    const [data, setData] = useState(null); // { providers, accounts }
    const [loadError, setLoadError] = useState('');
    const [notice, setNotice] = useState(() => readReturnNotice(location.search));
    const [busy, setBusy] = useState({}); // platform → 'connect' | 'manual'
    const [errors, setErrors] = useState({}); // platform → message
    const [manual, setManual] = useState({}); // platform → { handle, profileUrl, error, field }
    const [confirm, setConfirm] = useState(null); // { platform, account, busy, error }
    const [guidePlatform, setGuidePlatform] = useState('');
    const guideHeading = useRef(null);
    const mounted = useRef(true);
    const productId = currentProduct?.id;

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    // The backend keeps product.channels in step with the accounts; pick that up quietly
    const refreshProduct = useCallback(async () => {
        if (!productId) return;
        try {
            const product = await api.getProduct(productId);
            if (product?.id) replaceProduct(product);
        } catch (e) {
            // The accounts on this page are already current; the product catches up on next load
        }
    }, [productId, replaceProduct]);

    // Back from OAuth: say how it went once, then drop the query so a reload doesn't repeat it
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (!params.has('connected') && !params.has('error')) return;
        params.delete('connected');
        params.delete('error');
        const rest = params.toString();
        navigate(`${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`, { replace: true });
        refreshProduct();
    }, [location, navigate, refreshProduct]);

    const load = useCallback(async () => {
        setLoadError('');
        setData(null);
        try {
            const result = await api.getSocialAccounts();
            if (!mounted.current) return;
            setData({ providers: result?.providers || [], accounts: result?.accounts || [] });
        } catch (error) {
            if (mounted.current) setLoadError(errorMessage(error, 'We couldn\'t load your social accounts. Check your connection and try again.'));
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const setFor = (setter, platformId, value) => setter(current => {
        const next = { ...current };
        if (value === undefined) delete next[platformId];
        else next[platformId] = value;
        return next;
    });

    const connect = async (platform) => {
        setFor(setErrors, platform.id, undefined);
        setFor(setBusy, platform.id, 'connect');
        try {
            const { url } = await api.connectSocial(platform.id);
            if (!url) throw new Error('No sign-in link came back.');
            // Stays busy while the browser leaves for the platform's sign-in page
            window.location.assign(url);
        } catch (error) {
            setFor(setBusy, platform.id, undefined);
            setFor(setErrors, platform.id, errorMessage(error, `We couldn't start connecting ${platform.name}. Please try again.`));
        }
    };

    const openManual = (platform) => {
        setFor(setErrors, platform.id, undefined);
        setFor(setManual, platform.id, { handle: '', profileUrl: '', error: '', field: '' });
    };

    const changeManual = (platform, change) => setManual(current => ({
        ...current,
        // Typing clears the message about the field being fixed
        [platform.id]: { ...current[platform.id], ...change, error: '', field: '' },
    }));

    const submitManual = async (platform, event) => {
        event.preventDefault();
        const form = manual[platform.id];
        if (!form || busy[platform.id]) return;
        const checked = validateManual(platform, form.handle, form.profileUrl);
        if (checked.error) {
            setFor(setManual, platform.id, { ...form, error: checked.error, field: checked.field });
            return;
        }
        setFor(setBusy, platform.id, 'manual');
        try {
            const { account } = await api.addManualSocial({
                platform: platform.id,
                handle: checked.handle,
                ...(checked.profileUrl && { profileUrl: checked.profileUrl }),
            });
            if (!mounted.current) return;
            setData(current => ({
                ...current,
                accounts: [...current.accounts.filter(item => item.id !== account.id), account],
            }));
            setFor(setManual, platform.id, undefined);
            refreshProduct();
        } catch (error) {
            setFor(setManual, platform.id, { ...form, error: errorMessage(error, 'We couldn\'t save that account. Please try again.'), field: '' });
        } finally {
            if (mounted.current) setFor(setBusy, platform.id, undefined);
        }
    };

    const closeConfirm = useCallback(() => setConfirm(null), []);

    const disconnect = async () => {
        if (!confirm) return;
        const { account } = confirm;
        setConfirm(current => ({ ...current, busy: true, error: '' }));
        try {
            await api.deleteSocialAccount(account.id);
            if (!mounted.current) return;
            setData(current => ({ ...current, accounts: current.accounts.filter(item => item.id !== account.id) }));
            setConfirm(null);
            refreshProduct();
        } catch (error) {
            setConfirm(current => current && {
                ...current,
                busy: false,
                error: errorMessage(error, 'We couldn\'t disconnect this account. Please try again.'),
            });
        }
    };

    const showGuide = (platform) => {
        setGuidePlatform(platform.id);
        const heading = guideHeading.current;
        if (heading) {
            heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
            heading.focus({ preventScroll: true });
        }
    };

    const accounts = data?.accounts || [];
    const providers = data?.providers || [];
    // The guide opens on the first platform without an account
    const defaultGuide = (data && PLATFORMS.find(platform => !accountFor(accounts, platform.id))?.id) || PLATFORMS[0].id;
    const connectedCount = PLATFORMS.filter(platform => accountFor(accounts, platform.id)).length;

    const noticePlatform = notice?.platform ? platformName(notice.platform) : '';
    const noticeAccount = notice?.platform && accountFor(accounts, notice.platform);

    return (
        <>
            <div className="ls-page-header">
                <div>
                    <div className="ls-eyebrow">Product · {currentProduct?.name}</div>
                    <h1 className="ls-page-title">Social accounts</h1>
                    <p className="ls-social-sub">
                        Connect the accounts light speed will post to and track. Posting comes next; connecting now lets us read your results.
                    </p>
                </div>
                {/* Space kept while loading, so the count arriving doesn't push the cards down on phones */}
                <span className={`ls-chip is-medium ls-social-count${data ? '' : ' is-pending'}`} aria-hidden={!data || undefined}>
                    {connectedCount} of {PLATFORMS.length} linked
                </span>
            </div>

            {notice && (
                <Alert severity={notice.severity} onClose={() => setNotice(null)}>
                    {notice.severity === 'success' ? (
                        <>
                            <strong>{noticePlatform} is connected</strong>
                            {noticeAccount?.handle ? ` as @${String(noticeAccount.handle).replace(/^@/, '')}` : ''}.
                            {' '}We'll start reading results from your next post.
                        </>
                    ) : (
                        <>We couldn't connect that account: {notice.message}</>
                    )}
                </Alert>
            )}

            {loadError && (
                <Alert
                    severity="error"
                    action={<button type="button" className="ls-btn ls-btn-secondary" onClick={load}>Try again</button>}
                >
                    {loadError}
                </Alert>
            )}

            {!data && !loadError && (
                <div className="ls-social-grid" aria-busy="true" aria-label="Loading social accounts">
                    {PLATFORMS.map(platform => <CardSkeleton key={platform.id} />)}
                </div>
            )}

            {data && (
                <div className="ls-social-grid ls-stagger">
                    {PLATFORMS.map((platform, index) => {
                        const account = accountFor(accounts, platform.id);
                        return (
                            <div key={platform.id} className="ls-social-cell" style={{ '--i': index }}>
                                <PlatformCard
                                    platform={platform}
                                    provider={providers.find(provider => provider.id === platform.id)}
                                    account={account}
                                    busy={busy[platform.id]}
                                    error={errors[platform.id]}
                                    manual={manual[platform.id]}
                                    onConnect={() => connect(platform)}
                                    onOpenManual={() => openManual(platform)}
                                    onManualChange={change => changeManual(platform, change)}
                                    onManualSubmit={event => submitManual(platform, event)}
                                    onManualCancel={() => setFor(setManual, platform.id, undefined)}
                                    onDisconnect={() => setConfirm({ platform, account, busy: false, error: '' })}
                                    onGuide={() => showGuide(platform)}
                                    onDismissError={() => setFor(setErrors, platform.id, undefined)}
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            <GuideSection selected={guidePlatform || defaultGuide} onSelect={setGuidePlatform} headingRef={guideHeading} />

            {confirm && (
                <ConfirmDialog
                    platform={confirm.platform}
                    account={confirm.account}
                    isBusy={confirm.busy}
                    error={confirm.error}
                    onConfirm={disconnect}
                    onCancel={closeConfirm}
                />
            )}
        </>
    );
};

export default SocialAccountsScreen;
